import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Plus } from 'lucide-react';
import { WidgetDefinition } from '@/lib/widgets/types';
import { widgetRegistry } from '@/lib/widgets/registry';
import { DynamicIcon } from '@/components/ui/dynamic-icon';

interface WidgetSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  enabledSystems: string[];
  existingWidgets: string[];
  onAddWidget: (widgetType: string) => void;
}

export function WidgetSidebar({ 
  isOpen, 
  onClose, 
  enabledSystems,
  existingWidgets,
  onAddWidget 
}: WidgetSidebarProps) {
  const availableWidgets = widgetRegistry.getAvailableWidgets(enabledSystems)
    .filter(widget => !existingWidgets.includes(widget.type));
  
  console.log('🎯 WidgetSidebar Debug:', {
    enabledSystems,
    totalAvailableWidgets: availableWidgets.length,
    existingWidgets,
    availableWidgetTypes: availableWidgets.map(w => w.type),
    systemCodes: [...new Set(availableWidgets.map(w => w.systemCode || 'universal'))]
  });
  
  // Group widgets by system
  const widgetsBySystem = availableWidgets.reduce((acc, widget) => {
    const system = widget.systemCode || 'universal';
    if (!acc[system]) acc[system] = [];
    acc[system].push(widget);
    return acc;
  }, {} as Record<string, WidgetDefinition[]>);

  const systemNames: Record<string, string> = {
    universal: 'Widgets Universais',
    fleet: 'FleetONE',
    gps_tracking: 'TrackingONE',
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-96 sm:max-w-96">
        <SheetHeader>
          <SheetTitle>Adicionar Widgets</SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-100px)] mt-6">
          <div className="space-y-6">
            {Object.entries(widgetsBySystem).map(([systemCode, widgets]) => (
              <div key={systemCode}>
                <h3 className="font-semibold text-sm text-muted-foreground mb-3">
                  {systemNames[systemCode] || systemCode}
                </h3>
                
                <div className="space-y-3">
                  {widgets.map((widget) => (
                    <Card key={widget.type} className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <DynamicIcon name={widget.icon} className="h-4 w-4 text-primary" />
                            <CardTitle className="text-sm">{widget.title}</CardTitle>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => onAddWidget(widget.type)}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <CardDescription className="text-xs">
                          {widget.description}
                        </CardDescription>
                      </CardHeader>
                      
                      <CardContent className="pt-0">
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">
                            {widget.defaultSize.w}x{widget.defaultSize.h}
                          </Badge>
                          {widget.systemCode && (
                            <Badge variant="secondary" className="text-xs">
                              {systemNames[widget.systemCode]}
                            </Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {Object.keys(widgetsBySystem).indexOf(systemCode) < Object.keys(widgetsBySystem).length - 1 && (
                  <Separator className="my-4" />
                )}
              </div>
            ))}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}